import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Box,
  Chip,
  Divider
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Warning as WarningIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { deviceService } from '../services/deviceService';

const DeviceDeleteDialog = ({ 
  open, 
  device,
  onClose, 
  onSuccess, 
  onError 
}) => {
  const [deleting, setDeleting] = useState(false);
  const [findingsSummary, setFindingsSummary] = useState(null);
  const [loadingFindings, setLoadingFindings] = useState(false);

  // Fetch findings summary when dialog opens
  useEffect(() => {
    if (open && device) {
      fetchFindingsSummary();
    }
  }, [open, device]);

  const fetchFindingsSummary = async () => {
    if (!device) return;
    
    setLoadingFindings(true);
    try {
      const data = await deviceService.getDeletePreview(device.id);
      setFindingsSummary(data.findings_summary);
    } catch (error) {
      console.error('Failed to fetch findings summary:', error);
      // Continue with deletion dialog even if preview fails
      setFindingsSummary(null);
    } finally {
      setLoadingFindings(false);
    }
  };

  const handleClose = () => {
    if (!deleting) {
      setFindingsSummary(null);
      onClose();
    }
  };

  const handleDelete = async () => {
    if (!device) return;

    setDeleting(true);
    try {
      await deviceService.deleteDevice(device.id);
      onSuccess(`Device ${device.device_name} deleted successfully!`);
      onClose();
    } catch (err) {
      onError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  if (!device) return null;

  const hasFindingsData = findingsSummary && findingsSummary.total_findings > 0;
  const isLoading = loadingFindings || deleting;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <DeleteIcon color="error" />
          <Typography variant="h6" fontWeight={600}>
            Delete Device
          </Typography>
        </Stack>
      </DialogTitle>
      
      <DialogContent>
        <Stack spacing={3}>
          {/* Device Information */}
          <Paper sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Stack spacing={1}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Device Name:</Typography>
                <Typography variant="body2" fontWeight={500}>{device.device_name}</Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">UUID:</Typography>
                <Typography variant="body2" fontFamily="monospace" fontSize="0.875rem">
                  {device.device_uuid}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Vehicle ID:</Typography>
                <Typography variant="body2" fontWeight={500}>{device.vehicle_id || 'N/A'}</Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Network:</Typography>
                <Typography variant="body2" fontWeight={500}>
                  {device.network_name ? 
                    `${device.network_name} (${device.network_code})` : 
                    'N/A'
                  }
                </Typography>
              </Stack>
            </Stack>
          </Paper>

          {/* Findings Summary Loading */}
          {loadingFindings && (
            <Box display="flex" alignItems="center" gap={2}>
              <CircularProgress size={20} />
              <Typography>Analyzing device findings...</Typography>
            </Box>
          )}

          {/* Warning Alert for Devices with Findings */}
          {!loadingFindings && hasFindingsData && (
            <Alert 
              severity="warning" 
              icon={<WarningIcon />}
              sx={{ '& .MuiAlert-message': { width: '100%' } }}
            >
              <Typography variant="subtitle1" gutterBottom>
                ⚠️ This device has associated findings that will be permanently deleted:
              </Typography>
              
              <Box mt={2}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {findingsSummary.preliminary_findings > 0 && (
                    <Chip 
                      label={`${findingsSummary.preliminary_findings} Preliminary Findings`}
                      color="warning"
                      size="small"
                    />
                  )}
                  {findingsSummary.draft_findings > 0 && (
                    <Chip 
                      label={`${findingsSummary.draft_findings} Draft Findings`}
                      color="info"
                      size="small"
                    />
                  )}
                  {findingsSummary.repeated_findings > 0 && (
                    <Chip 
                      label={`${findingsSummary.repeated_findings} Repeated Findings`}
                      color="secondary"
                      size="small"
                    />
                  )}
                  {findingsSummary.burst_findings > 0 && (
                    <Chip 
                      label={`${findingsSummary.burst_findings} Burst Findings`}
                      color="primary"
                      size="small"
                    />
                  )}
                  {findingsSummary.validated_defects > 0 && (
                    <Chip 
                      label={`${findingsSummary.validated_defects} Validated Defects`}
                      color="error"
                      size="small"
                    />
                  )}
                </Stack>
              </Box>

              <Divider sx={{ my: 2 }} />
              
              <Typography variant="body2" fontWeight="bold">
                Total Data to be Deleted: {findingsSummary.total_findings} records
              </Typography>
            </Alert>
          )}

          {/* Info Alert for Clean Devices */}
          {!loadingFindings && !hasFindingsData && findingsSummary !== null && (
            <Alert severity="info" icon={<InfoIcon />}>
              <Typography>
                This device has no associated findings. It can be safely deleted without affecting any detection data.
              </Typography>
            </Alert>
          )}

          {/* Final Warning */}
          <Alert severity="error">
            <Typography variant="subtitle2" gutterBottom>
              ⚠️ This action cannot be undone
            </Typography>
            <Typography variant="body2">
              {hasFindingsData 
                ? 'Deleting this device will permanently remove all associated findings and defects from the system.'
                : 'The device will be permanently removed from the system.'
              }
            </Typography>
          </Alert>
        </Stack>
      </DialogContent>
      
      <DialogActions sx={{ p: 3 }}>
        <Button 
          onClick={handleClose}
          disabled={isLoading}
          size="large"
        >
          Cancel
        </Button>
        <Button 
          onClick={handleDelete}
          variant="contained"
          color="error"
          disabled={isLoading}
          startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
          size="large"
        >
          {deleting 
            ? 'Deleting...' 
            : hasFindingsData 
              ? `Delete Device & ${findingsSummary.total_findings} Findings`
              : 'Delete Device'
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeviceDeleteDialog; 